import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Box, Button, Typography } from '@mui/material';
import type { ReactNode } from 'react';

interface EmptyStateProps {
  title: string;
  /** Say what to do next, not just that there is nothing here. */
  description?: string;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  compact?: boolean;
}

/**
 * The standard "nothing here" panel (brief §6).
 *
 * Used for a genuinely empty collection. A filtered-to-nothing grid is a different situation
 * and says so, because "no results" and "no data at all" call for different next steps.
 */
export function EmptyState({ title, description, icon, action, compact }: EmptyStateProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        py: compact ? 3 : 6,
        px: 2,
        color: 'text.secondary',
      }}
    >
      {icon ?? <InboxOutlinedIcon sx={{ fontSize: compact ? 32 : 48, opacity: 0.5 }} />}
      <Typography variant={compact ? 'subtitle2' : 'h4'} color="text.primary">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {action && (
        <Button size="small" variant="outlined" onClick={action.onClick} sx={{ mt: 1 }}>
          {action.label}
        </Button>
      )}
    </Box>
  );
}
